/**
 * Pure hex-territory layout for the Explore Map (`ExplorationMap.tsx`).
 *
 * No React, no pixels — this module only computes axial `(q, r)` cells. The
 * component maps cells to pixels with `hexToPixel`, so this file (and its
 * tests) stay resolution-independent. Recomputed from the full product map
 * on every render (Decision 5: no persisted positions) — contiguity and
 * determinism come from the algorithm, not from caching.
 *
 * Algorithm (Decision 6, `specs/pending/explore-map.md`):
 * - Every area gets a "blob" of `n_a = max(1, capability count)` hex cells
 *   (an empty area gets one placeholder cell, no tile). The "Unassigned"
 *   bucket (areaId `null`) is added last, only when there are capabilities
 *   with `areaId === null`, and never gets a placeholder.
 * - `R` is the smallest `k >= 1` with the k-radius hex number
 *   `3k² + 3k + 1 >= max(n_a)` across every blob.
 * - Seeds are the unit hex-ring spiral scaled by `2R + 1`, assigned in
 *   ascending area `code` order (Unassigned last). Because hex distance is a
 *   norm, any two seeds are `> 2R` apart, so a radius-`R` disc around one
 *   seed never overlaps another blob's disc — no blob can ever be boxed in.
 * - Growth is round-robin over blobs: each blob's turn claims the unclaimed
 *   hex adjacent to any of its current cells with the smallest axial
 *   distance to its own seed (ties by `(q, r)` ascending), until it owns
 *   exactly `n_a` cells.
 * - Capabilities fill a blob's cells in claim order, sorted by `codeSeq`
 *   ascending (null last, then by `id`).
 *
 * **Amended 2026-09-14 (v3, after design review):** the widely-spaced grown
 * blobs from above are still an archipelago — a **compaction** pass now
 * slides each later blob toward the origin, in seed order, until it touches
 * the already-placed landmass, producing one packed cluster of tiles with no
 * ground/fill cells at all. Blob 0 never moves. For blob `i > 0`: repeatedly
 * translate *all* of its cells by one axial step — the `DIRECTIONS` entry
 * that most reduces `hexDistance(seed, origin)`, ties by lowest direction
 * index — and stop as soon as (a) any of its cells becomes 6-neighbour
 * adjacent to an already-placed cell (keep the step), or (b) a step would
 * land on an already-placed cell (undo it — the pre-step position is then
 * necessarily adjacent, per (a)). No two blobs can ever overlap: the
 * overlap check is against every earlier blob's final position before this
 * blob moves at all. A **settle** pass then runs (v3 tightening): compaction
 * alone can leave a blob touching the landmass at just one distant corner,
 * so this repeats full sweeps over blobs `1..n-1` nudging each one step
 * closer to the origin whenever a step stays non-overlapping, keeps it
 * adjacent to some other blob, and strictly reduces its seed's distance —
 * largest reduction wins, ties by lowest direction index — until a full
 * sweep moves nothing. `HexLayout.areas[i].cells` is exactly that blob's
 * (translated) tile cells, in claim order — identical in length and order
 * to `tiles`' entries for that area, since there is no ground cell to add.
 *
 * @module features/exploration/map/hexLayout
 */

import type { ProductArea, ProductCapability } from '../../product-map/types';

export interface HexCell {
  q: number;
  r: number;
}

export interface HexLayout {
  areas: { areaId: string | null; label: string; cells: HexCell[] }[];
  tiles: { capabilityId: string; areaId: string | null; cell: HexCell }[];
}

const UNASSIGNED_LABEL = 'Unassigned';
const ORIGIN: HexCell = { q: 0, r: 0 };

/** Stable string key for a cell — used as a `Map`/`Set` key throughout this module and by callers. */
export function cellKey(cell: HexCell): string {
  return `${cell.q},${cell.r}`;
}

/**
 * The six axial neighbour directions (pointy-top), in a fixed order —
 * `generateSpiral`'s ring walk below is order sensitive, and the compaction
 * pass's "lowest direction index" tie-break depends on this order too.
 */
export const DIRECTIONS: HexCell[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export function hexDistance(a: HexCell, b: HexCell): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/**
 * Every cell of the hex ring at the given radius (radius >= 1), in a fixed
 * deterministic walk order: start at `(radius, 0)` (direction 0 scaled by
 * radius — this is what pins `spiral(1) === (1, 0)`), then walk the ring
 * edges using directions `[2, 3, 4, 5, 0, 1]` (i.e. rotated +2 from the
 * start corner), `radius` steps each. This traces the six ring edges without
 * revisiting the start corner.
 */
function hexRing(radius: number): HexCell[] {
  const walkOrder = [2, 3, 4, 5, 0, 1];
  const results: HexCell[] = [];
  let cursor: HexCell = { q: DIRECTIONS[0].q * radius, r: DIRECTIONS[0].r * radius };
  for (const dirIndex of walkOrder) {
    const dir = DIRECTIONS[dirIndex];
    for (let step = 0; step < radius; step++) {
      results.push(cursor);
      cursor = { q: cursor.q + dir.q, r: cursor.r + dir.r };
    }
  }
  return results;
}

/** The first `count` hexes in spiral order: `spiral(0) = (0,0)`, then ring 1, ring 2, ... */
function generateSpiral(count: number): HexCell[] {
  const result: HexCell[] = [{ q: 0, r: 0 }];
  let ring = 1;
  while (result.length < count) {
    result.push(...hexRing(ring));
    ring++;
  }
  return result.slice(0, count);
}

/** Smallest k >= 1 with the k-radius hex number `3k² + 3k + 1 >= maxN`. */
function radiusFor(maxN: number): number {
  let k = 1;
  while (3 * k * k + 3 * k + 1 < maxN) k++;
  return k;
}

interface BlobSpec {
  areaId: string | null;
  label: string;
  n: number;
  caps: ProductCapability[];
}

interface Blob extends BlobSpec {
  /**
   * The blob's original spiral seed — read during growth (`nextClaim`) as
   * the point new cells grow toward. Stale after growth: `compactBlobs` and
   * `settleBlobs` translate `cells` in place but never touch this field, so
   * only `cells[0]` is authoritative for a blob's position from compaction
   * onward.
   */
  seed: HexCell;
  cells: HexCell[];
}

function sortCapabilities(caps: readonly ProductCapability[]): ProductCapability[] {
  return [...caps].sort((a, b) => {
    const aSeq = a.codeSeq ?? Number.POSITIVE_INFINITY;
    const bSeq = b.codeSeq ?? Number.POSITIVE_INFINITY;
    if (aSeq !== bSeq) return aSeq - bSeq;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** The unclaimed hex adjacent to any of `blob.cells` with smallest distance to `blob.seed`, ties by (q, r) ascending. */
function nextClaim(blob: Blob, claimed: ReadonlySet<string>): HexCell {
  let best: HexCell | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  const seen = new Set<string>();
  for (const cell of blob.cells) {
    for (const dir of DIRECTIONS) {
      const candidate: HexCell = { q: cell.q + dir.q, r: cell.r + dir.r };
      const k = cellKey(candidate);
      if (claimed.has(k) || seen.has(k)) continue;
      seen.add(k);
      const dist = hexDistance(candidate, blob.seed);
      const better =
        dist < bestDist ||
        (dist === bestDist &&
          best !== null &&
          (candidate.q < best.q || (candidate.q === best.q && candidate.r < best.r)));
      if (better || best === null) {
        best = candidate;
        bestDist = dist;
      }
    }
  }
  if (!best) {
    // Unreachable given the seed-spacing invariant (Decision 6) — fails
    // loudly rather than silently truncating a blob's growth if that
    // invariant is ever violated.
    throw new Error('hexLayout: no unclaimed hex adjacent to blob — seed spacing invariant violated');
  }
  return best;
}

/** True if any of `cells` is 6-neighbour adjacent to a cell already in `placed`. */
function touchesPlaced(cells: readonly HexCell[], placed: ReadonlySet<string>): boolean {
  for (const cell of cells) {
    for (const dir of DIRECTIONS) {
      if (placed.has(cellKey({ q: cell.q + dir.q, r: cell.r + dir.r }))) return true;
    }
  }
  return false;
}

/**
 * True if the union of `setKeys` (a Set of `"q,r"` cell keys) and `extra`
 * cells forms exactly one 6-connected component — BFS from an arbitrary
 * start cell in the union, then compare visited count to union size.
 */
function isUnionConnected(setKeys: ReadonlySet<string>, extra: readonly HexCell[]): boolean {
  const union = new Set<string>(setKeys);
  for (const c of extra) union.add(cellKey(c));
  if (union.size === 0) return true;

  const start = union.values().next().value as string;
  const visited = new Set<string>([start]);
  const queue: string[] = [start];
  while (queue.length > 0) {
    const curKey = queue.shift()!;
    const commaIndex = curKey.indexOf(',');
    const cur: HexCell = { q: Number(curKey.slice(0, commaIndex)), r: Number(curKey.slice(commaIndex + 1)) };
    for (const dir of DIRECTIONS) {
      const neighbourKey = cellKey({ q: cur.q + dir.q, r: cur.r + dir.r });
      if (union.has(neighbourKey) && !visited.has(neighbourKey)) {
        visited.add(neighbourKey);
        queue.push(neighbourKey);
      }
    }
  }
  return visited.size === union.size;
}

/**
 * A blob shape narrow enough for `compactBlobs`/`settleBlobs` to operate on
 * (and for tests to construct directly) — `cells[0]` doubles as the blob's
 * current seed position throughout both passes, since growth always claims
 * the seed first (`buildHexLayout`'s `cells: [seedPoints[i]]`).
 */
interface CompactableBlob {
  cells: HexCell[];
}

/**
 * Compaction pass (Decision 6 amendment, v3): slides each blob after the
 * first toward the origin, in place, until it touches the already-placed
 * landmass. Mutates `blob.cells` for every blob after the first. Exported
 * for direct testing (`hexLayout.logic.test.ts`) — `buildHexLayout` is the
 * only real caller.
 */
export function compactBlobs(blobs: readonly CompactableBlob[]): void {
  if (blobs.length === 0) return;
  const placed = new Set<string>(blobs[0].cells.map(cellKey));

  for (let i = 1; i < blobs.length; i++) {
    const blob = blobs[i];
    let cells = blob.cells;

    // The only exit is `bestDirIndex === -1` (no improving direction left).
    // A step can never collide with `placed`: colliding would mean some
    // translated cell equals a placed cell, and since it's one axial step
    // away, the pre-step cell is then 6-adjacent to that same placed cell —
    // i.e. `touchesPlaced(cells, placed)` would already have been true and
    // the loop would have stopped before this step was attempted.
    while (!touchesPlaced(cells, placed)) {
      const currentSeed = cells[0];
      const seedDistance = hexDistance(currentSeed, ORIGIN);
      let bestDirIndex = -1;
      let bestDist = seedDistance;
      for (let d = 0; d < DIRECTIONS.length; d++) {
        const candidateSeed = { q: currentSeed.q + DIRECTIONS[d].q, r: currentSeed.r + DIRECTIONS[d].r };
        const dist = hexDistance(candidateSeed, ORIGIN);
        if (dist < bestDist) {
          bestDist = dist;
          bestDirIndex = d;
        }
      }
      if (bestDirIndex === -1) {
        // Unreachable while `blobs[0]` exists: `seedDistance > 0` always has
        // an improving direction toward the origin, and the loop guard
        // already stopped once `seedDistance === 0` would be the only way
        // to have no improving move left. Same doctrine as `nextClaim`: fail
        // loudly rather than silently truncating a blob's compaction.
        throw new Error('hexLayout: compactBlobs found no improving direction while still not touching placed');
      }

      const dir = DIRECTIONS[bestDirIndex];
      cells = cells.map((c) => ({ q: c.q + dir.q, r: c.r + dir.r }));
    }

    blob.cells = cells;
    for (const cell of cells) placed.add(cellKey(cell));
  }
}

/**
 * Settle pass (v3 tightening, after design review): compaction alone can
 * leave a blob touching the landmass at just one distant corner while a
 * shorter path around it exists — this pass keeps nudging each blob after
 * the first (in seed order, repeated full sweeps) one step closer to the
 * origin as long as the step (a) doesn't overlap any other blob's current
 * cells, (b) keeps the *entire* union of cells 6-connected as one component
 * (not just "the mover stays adjacent to something" — round 4 review: that
 * weaker check let the mover settle away from a third blob that was only
 * ever attached through the mover's old position, orphaning it), and (c)
 * strictly reduces its seed's distance to the origin; among the up-to-6
 * steps that qualify, it takes the one with the largest reduction, ties by
 * lowest direction index. A blob is left alone once no qualifying step
 * remains. Bounded: every accepted move strictly reduces a non-negative
 * integer distance, so full sweeps repeating until one moves nothing must
 * terminate. Exported for direct testing, same as `compactBlobs`.
 */
export function settleBlobs(blobs: readonly CompactableBlob[]): void {
  if (blobs.length <= 1) return;

  let movedAnyInSweep = true;
  while (movedAnyInSweep) {
    movedAnyInSweep = false;

    for (let i = 1; i < blobs.length; i++) {
      const blob = blobs[i];

      // Built once per blob per sweep, not per accepted step: while this
      // inner loop only moves `blob` itself, every other blob's cells stay
      // put, so this Set stays valid for every step below.
      const otherCells = new Set<string>();
      for (let j = 0; j < blobs.length; j++) {
        if (j === i) continue;
        for (const cell of blobs[j].cells) otherCells.add(cellKey(cell));
      }

      let moved = true;
      while (moved) {
        moved = false;

        const currentSeed = blob.cells[0];
        const currentDist = hexDistance(currentSeed, ORIGIN);
        let bestDirIndex = -1;
        let bestReduction = 0;

        for (let d = 0; d < DIRECTIONS.length; d++) {
          const dir = DIRECTIONS[d];
          const translated = blob.cells.map((c) => ({ q: c.q + dir.q, r: c.r + dir.r }));
          if (translated.some((c) => otherCells.has(cellKey(c)))) continue; // (a) no overlap
          if (!isUnionConnected(otherCells, translated)) continue; // (b) whole union stays one component
          const newDist = hexDistance({ q: currentSeed.q + dir.q, r: currentSeed.r + dir.r }, ORIGIN);
          const reduction = currentDist - newDist;
          if (reduction <= 0) continue;
          if (reduction > bestReduction) {
            bestReduction = reduction;
            bestDirIndex = d;
          }
        }

        if (bestDirIndex !== -1) {
          const dir = DIRECTIONS[bestDirIndex];
          blob.cells = blob.cells.map((c) => ({ q: c.q + dir.q, r: c.r + dir.r }));
          moved = true;
          movedAnyInSweep = true;
        }
      }
    }
  }
}

export function buildHexLayout(areas: ProductArea[], capabilities: ProductCapability[]): HexLayout {
  const byArea = new Map<string, ProductCapability[]>();
  const unassigned: ProductCapability[] = [];
  for (const cap of capabilities) {
    if (cap.areaId) {
      const list = byArea.get(cap.areaId);
      if (list) list.push(cap);
      else byArea.set(cap.areaId, [cap]);
    } else {
      unassigned.push(cap);
    }
  }

  const sortedAreas = [...areas].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));

  const blobSpecs: BlobSpec[] = sortedAreas.map((area) => ({
    areaId: area.id,
    label: area.title,
    n: Math.max(1, byArea.get(area.id)?.length ?? 0),
    caps: byArea.get(area.id) ?? [],
  }));
  if (unassigned.length > 0) {
    blobSpecs.push({ areaId: null, label: UNASSIGNED_LABEL, n: unassigned.length, caps: unassigned });
  }

  if (blobSpecs.length === 0) {
    return { areas: [], tiles: [] };
  }

  const maxN = Math.max(...blobSpecs.map((b) => b.n));
  const radius = radiusFor(maxN);
  const scale = 2 * radius + 1;
  const seedPoints = generateSpiral(blobSpecs.length).map((c) => ({ q: c.q * scale, r: c.r * scale }));

  const blobs: Blob[] = blobSpecs.map((spec, i) => ({
    ...spec,
    seed: seedPoints[i],
    cells: [seedPoints[i]],
  }));

  const claimed = new Set<string>();
  for (const blob of blobs) claimed.add(cellKey(blob.seed));

  while (blobs.some((b) => b.cells.length < b.n)) {
    for (const blob of blobs) {
      if (blob.cells.length >= blob.n) continue;
      const next = nextClaim(blob, claimed);
      claimed.add(cellKey(next));
      blob.cells.push(next);
    }
  }

  compactBlobs(blobs);
  settleBlobs(blobs);

  const tiles: HexLayout['tiles'] = [];
  for (const blob of blobs) {
    const sortedCaps = sortCapabilities(blob.caps);
    for (let i = 0; i < sortedCaps.length; i++) {
      tiles.push({ capabilityId: sortedCaps[i].id, areaId: blob.areaId, cell: blob.cells[i] });
    }
  }

  const areasOut = blobs.map((b) => ({ areaId: b.areaId, label: b.label, cells: b.cells }));

  return { areas: areasOut, tiles };
}

/** Every hex within `maxDist` of `center` (inclusive), unordered. */
function hexesWithin(center: HexCell, maxDist: number): HexCell[] {
  const results: HexCell[] = [];
  for (let dq = -maxDist; dq <= maxDist; dq++) {
    for (let dr = -maxDist; dr <= maxDist; dr++) {
      const candidate = { q: center.q + dq, r: center.r + dr };
      if (hexDistance(candidate, center) <= maxDist) results.push(candidate);
    }
  }
  return results;
}

/**
 * Decision 10 (v3 amendment, round 3 review): an unclaimed hex (not a layout
 * cell of any area) within hex distance ≤ 2 of any of `areaId`'s blob cells —
 * not just distance 1 — because packing can fully enclose a small blob (e.g.
 * six 1-tile areas ringing a 1-tile origin area) so every distance-1
 * neighbour is already claimed by another area. Picks the smallest distance
 * to the blob (the minimum over the blob's own cells), ties by greatest `r`
 * then greatest `q` (bottom edge, deterministic), skipping cells in `taken`.
 * `taken` starts empty for the caller's first lead in an area; the caller
 * adds each returned cell to `taken` before placing the next lead so two
 * leads in one area land on distinct cells. Returns `null` when the area
 * isn't in the layout, or — practically never, only when no unclaimed hex
 * exists within 2 of the blob — no candidate is available.
 */
export function placeLead(layout: HexLayout, areaId: string | null, taken: ReadonlySet<string>): HexCell | null {
  const areaEntry = layout.areas.find((a) => a.areaId === areaId);
  if (!areaEntry) return null;

  const layoutCells = new Set<string>();
  for (const a of layout.areas) for (const c of a.cells) layoutCells.add(cellKey(c));

  const seen = new Set<string>();
  let best: HexCell | null = null;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const blobCell of areaEntry.cells) {
    for (const candidate of hexesWithin(blobCell, 2)) {
      const k = cellKey(candidate);
      if (layoutCells.has(k) || taken.has(k) || seen.has(k)) continue;
      seen.add(k);

      const dist = Math.min(...areaEntry.cells.map((c) => hexDistance(candidate, c)));
      const better =
        dist < bestDist ||
        (dist === bestDist &&
          best !== null &&
          (candidate.r > best.r || (candidate.r === best.r && candidate.q > best.q)));
      if (better || best === null) {
        best = candidate;
        bestDist = dist;
      }
    }
  }
  return best;
}

/** Pointy-top axial → pixel. Used by the component only. */
export function hexToPixel(cell: HexCell, radius: number): { x: number; y: number } {
  return {
    x: radius * Math.sqrt(3) * (cell.q + cell.r / 2),
    y: radius * 1.5 * cell.r,
  };
}
